import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { supabase } from '@/lib/supabaseClient';
import { DocumentMetadata } from '@/types';
import { Progress } from '@/components/ui/progress';
import { UploadCloud, File as FileIcon, X } from 'lucide-react';

interface UploadNewVersionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  document: (DocumentMetadata & { versionNumber?: number; version?: number }) | null;
  onSuccess: () => void;
}

export default function UploadNewVersionDialog({ isOpen, onClose, document, onSuccess }: UploadNewVersionDialogProps) {
  const { toast } = useToast();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelection = (file: File) => {
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      handleFileSelection(event.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !document) {
      toast({
        title: 'No file selected',
        description: 'Please select a file to upload as a new version.',
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);
    setProgress(10);

    try {
      const metadataResponse = await apiRequest(
        'POST',
        `/api/documents/${document.documentMetadataId}/versions`,
        {
          fileName: selectedFile.name,
          fileSize: selectedFile.size,
          contentType: selectedFile.type || 'application/octet-stream',
        }
      );
      setProgress(30);

      if (!metadataResponse.ok) {
        const errorData = await metadataResponse.json().catch(() => ({ message: 'Failed to parse error response from server.' }));
        throw new Error(errorData.message || `Failed to create version metadata (status: ${metadataResponse.status})`);
      }
      
      const versionData = await metadataResponse.json();
      setProgress(50);

      const uploadPath = versionData.uploadPath;
      if (!uploadPath) {
        throw new Error('Upload path not provided by the server.');
      }
      
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(uploadPath, selectedFile, {
          cacheControl: '3600',
          upsert: false,
        });
      setProgress(80);

      if (uploadError) {
        console.error('Supabase storage upload error for new version:', uploadError);
        throw new Error(`File upload failed: ${uploadError.message}`);
      }

      setProgress(100);
      toast({
        title: 'Upload Successful',
        description: `Version ${versionData.versionNumber} of ${selectedFile.name} uploaded.`,
      });
      onSuccess();
    } catch (error) {
      let errorMessage = 'An unexpected error occurred.';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      console.error('Upload new version error:', error);
      toast({
        title: 'Upload Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };
  
  const clearSelectedFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
      setUploading(false);
      setProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isOpen]);

  const currentVersionDisplay = document?.versionNumber || document?.version || 'N/A';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!uploading && !open) onClose(); 
      if (uploading && !open) { /* Optionally warn user or prevent close */ }
    }}>
      <DialogContent 
        className="sm:max-w-[425px] md:max-w-[550px]"
        onPointerDownOutside={(e) => { if (uploading) e.preventDefault(); }}
        onInteractOutside={(e) => { if (uploading) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>Upload New Version</DialogTitle>
          {document && <DialogDescription>Replacing: {document.fileName} (Current Version: {currentVersionDisplay})</DialogDescription>}
        </DialogHeader>
        
        <div className="py-4 space-y-4">
          {!selectedFile ? (
            <div
              className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-colors"
              onClick={() => !uploading && fileInputRef.current?.click()}
            >
              <UploadCloud className="w-12 h-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">Click to browse</p>
              <Input
                type="file"
                ref={fileInputRef}
                className="hidden"
                onChange={handleFileChange}
                disabled={uploading}
              />
            </div>
          ) : (
            <div className="p-4 border rounded-md bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 overflow-hidden">
                  <FileIcon className="w-6 h-6 text-blue-500 flex-shrink-0" />
                  <div className="overflow-hidden">
                    <p className="text-sm font-medium text-gray-700 truncate">{selectedFile.name}</p>
                    <p className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(2)} KB</p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={!uploading ? clearSelectedFile : undefined} disabled={uploading}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {uploading && (
            <div className="space-y-1 pt-2">
              <div className="flex justify-between text-sm text-neutral-600">
                <span>Uploading {selectedFile?.name}...</span>
                <span>{progress}%</span>
              </div>
              <Progress value={progress} className="w-full h-2" />
            </div>
          )}

        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={uploading}>Cancel</Button>
          <Button onClick={handleUpload} disabled={!selectedFile || uploading}>
            {uploading ? `Uploading...` : 'Upload New Version'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 